import { yieldToMainThread } from '@theme/utilities';
import { Component } from '@theme/component';

/**
 * FlyToCart custom element for animating product images to cart
 * This component creates a visual effect of a product "flying" to the cart when added
 */
class FlyToCart extends Component {
  /** @type {Element} */
  source;

  /** @type {boolean} */
  useSourceSize = false;

  /** @type {Element} */
  destination;

  connectedCallback() {
    super.connectedCallback();

    if (!this.source || !this.destination) {
      this.remove();
      return;
    }

    // Safety net: whatever happens to the animation below, a clone must never
    // outlive it and sit in the DOM.
    this.#removeTimeout = setTimeout(() => this.remove(), 2000);

    /** @type {DOMRectReadOnly | null} */
    let sourceRect = null;
    /** @type {DOMRectReadOnly | null} */
    let destinationRect = null;

    // The initial observations may arrive in separate callback batches (one
    // per observe() call), so keep observing until both rects are in.
    const intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.target === this.source) {
          sourceRect = entry.boundingClientRect;
        } else if (entry.target === this.destination) {
          destinationRect = entry.boundingClientRect;
        }
      });

      if (sourceRect && destinationRect) {
        intersectionObserver.disconnect();
        this.#animate(sourceRect, destinationRect);
      }
    });
    intersectionObserver.observe(this.source);
    intersectionObserver.observe(this.destination);
  }

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #removeTimeout;

  disconnectedCallback() {
    super.disconnectedCallback?.();
    if (this.#removeTimeout) clearTimeout(this.#removeTimeout);
  }

  /**
   * Animates the flying thingy along the bezier curve.
   * @param {DOMRectReadOnly} sourceRect - The bounding client rect of the source.
   * @param {DOMRectReadOnly} destinationRect - The bounding client rect of the destination.
   */
  #animate = async (sourceRect, destinationRect) => {
    //Define bezier curve points
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2,
      y: sourceRect.top + sourceRect.height / 2,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2,
      y: destinationRect.top + destinationRect.height / 2,
    };

    // Position the flying thingy back to the start point
    if (this.useSourceSize) {
      this.style.setProperty('--width', `${sourceRect.width}px`);
      this.style.setProperty('--height', `${sourceRect.height}px`);
    }
    this.style.setProperty('--start-x', `${startPoint.x}px`);
    this.style.setProperty('--start-y', `${startPoint.y}px`);
    this.style.setProperty('--travel-x', `${endPoint.x - startPoint.x}px`);
    this.style.setProperty('--travel-y', `${endPoint.y - startPoint.y}px`);

    await yieldToMainThread();

    await Promise.allSettled(this.getAnimations().map((a) => a.finished));
    this.remove();
  };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}
